import { useController } from "react-hook-form";
import type { Lens } from "@hookform/lenses";
import { PresetsList } from "@design-lab/widgets/transfers-sizes";
import { PresetForm } from "./PresetForm";
import { CustomPresetForm } from "./CustomPresetForm";
import type { TransferTypeData } from "../../model/transferLabTypes";
import { createPresetData, createCustomPreset } from "../../model/formHelpers";

export type TransferTypeFormProps = {
  transferTypeId: string;
  transferTypeLens: Lens<TransferTypeData>;
  // Props for pricing
  artworkId?: string;
  enhancementTier?: string;
  aspectRatio?: number | undefined;
  bounds?:
    | {
        minWidthInches: number;
        minHeightInches: number;
        maxWidthInches: number;
        maxHeightInches: number;
      }
    | undefined;
};

export function TransferTypeForm({
  transferTypeId,
  transferTypeLens,
  artworkId,
  enhancementTier,
  aspectRatio,
  bounds
}: TransferTypeFormProps) {
  // Get transfer type data using controller with lens
  const { field: transferTypeField } = useController(transferTypeLens.interop());

  // Safely extract values with defaults to handle initial undefined state
  const transferTypeData = transferTypeField.value || {};
  const presets = transferTypeData.presets || {};
  const presetOrder = transferTypeData.presetOrder || [];
  const customPreset = transferTypeData.customPreset || null;

  const handlePresetToggle = (presetId: string) => {
    if (presetId === "custom") {
      // Handle custom preset toggle
      if (customPreset) {
        // Remove custom preset
        transferTypeField.onChange({
          ...transferTypeData,
          customPreset: null
        });
      } else {
        // Add custom preset
        transferTypeField.onChange({
          ...transferTypeData,
          customPreset: createCustomPreset(transferTypeId, aspectRatio, bounds)
        });
      }
    } else if (presets[presetId]) {
      // Remove regular preset
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [presetId]: _, ...remainingPresets } = presets;
      transferTypeField.onChange({
        ...transferTypeData,
        presets: remainingPresets,
        presetOrder: presetOrder.filter((id: string) => id !== presetId)
      });
    } else {
      // Add regular preset
      const newPreset = createPresetData(presetId, aspectRatio);
      transferTypeField.onChange({
        ...transferTypeData,
        presets: { ...presets, [presetId]: newPreset },
        presetOrder: [...presetOrder, presetId]
      });
    }
  };

  const handleRemovePreset = (presetId: string) => {
    handlePresetToggle(presetId);
  };

  // Build selected presets list including custom
  const selectedPresets = [...presetOrder];
  if (customPreset) {
    selectedPresets.push("custom");
  }

  return (
    <>
      {/* Presets section */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-gray-900">Select Size Presets</h3>
        <PresetsList selectedPresets={selectedPresets} onPresetSelect={handlePresetToggle} />
      </div>

      {/* Selected presets forms */}
      {(presetOrder.length > 0 || customPreset) && (
        <div className="space-y-4">
          {/* Custom preset - always on top */}
          {customPreset && (
            <CustomPresetForm
              customPresetId={customPreset.id}
              customPresetLens={transferTypeLens.focus("customPreset")}
              {...(artworkId && { artworkId })}
              transferTypeId={transferTypeId}
              {...(enhancementTier && { enhancementTier })}
              aspectRatio={aspectRatio}
              bounds={bounds}
            />
          )}

          {/* Regular presets */}
          {presetOrder.map((presetId: string) => (
            <PresetForm
              key={presetId}
              presetId={presetId}
              presetLens={transferTypeLens.focus(`presets.${presetId}`)}
              onRemove={() => handleRemovePreset(presetId)}
              {...(artworkId && { artworkId })}
              transferTypeId={transferTypeId}
              {...(enhancementTier && { enhancementTier })}
            />
          ))}
        </div>
      )}
    </>
  );
}
